import React from 'react'
import { Formik } from 'formik'
import { Link } from 'react-router-dom'
import { Wrapper, Button, Grid, P } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import styled from 'styled-components'
import startCase from 'lodash/startCase'
import { dayjsLondon } from 'lib/dayjs'

import { useFetchArrayResource, RESOURCES } from 'api'
import StyledGridRow from 'components/list/grid-row'
import StyledLoadingRow from 'components/list/loading-row'
import NoResults from 'components/list/no-results'
import { SortableColumn } from 'components/list/sortable-column'
import useSearchParamManager, { SortValue } from 'hooks/useSearchParamManager'
import { type Voucher, type VoucherAttributes } from 'lib/models/voucher'
import { MAP_BRAND_ID_TO_NAME } from 'config/brands'
import FilterPanel from './filter-panel'

const StyledGridColumn = styled.li`
  font-size: ${FONT.SIZE.S};
  min-width: 0;
`

const StyledDataColumn = styled(StyledGridColumn)`
  overflow-wrap: anywhere;
`

const StyledCenteredColumn = styled(StyledGridColumn)`
  text-align: center;
`

const StyledCenteredDataColumn = styled(StyledDataColumn)`
  text-align: center;
`

const VoucherHeaderRow = styled(StyledGridRow)`
  li {
    white-space: nowrap;
  }
`

const VoucherDataRow = styled(StyledGridRow)`
  &:hover {
    background-color: ${COLOR.BACKGROUND.FOG};
  }
`

const RowLink = styled(Link)`
  display: contents;
  color: inherit;
  text-decoration: none;
`

const StyledHeaderRow = styled(Wrapper)`
  display: flex;
  align-items: center;
  margin-bottom: ${GTR.M};
`

const StyledCreateButton = styled(Wrapper)`
  margin-left: auto;
`

const StickyGridItem = styled(Grid.Item)`
  position: sticky;
  top: 0;
  max-height: fit-content;
`

const GRID_TEMPLATE_COLUMNS =
  '1.2fr 0.85fr 1fr 1fr 0.75fr 1.15fr 100px 64px 95px'

const INITIAL_FILTER_VALUES = {
  brandId: [],
  productName: [],
  discountType: [],
  status: [],
}

const SORT_CONFIG: SortValue<VoucherAttributes> = {
  sortColumn: 'code',
  sortOrder: 'asc',
}

const formatDiscount = (
  discountType: string,
  discountValue: number
): string => {
  if (discountType === 'gift_card') return 'Gift card'
  if (discountType === 'percentage') return `${discountValue}%`
  return `£${(discountValue / 100).toFixed(2)}`
}

const Row = ({ voucher }: { voucher: Voucher }): React.ReactElement => {
  const {
    id,
    code,
    productName,
    brandId,
    discountType,
    discountValue,
    expiresAt,
    usageLimit,
    referrals,
    usage,
  } = voucher.attributes

  const brandName = brandId ? MAP_BRAND_ID_TO_NAME[brandId] : ''
  return (
    <VoucherDataRow gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
      <RowLink to={`/admin/vouchers/${id}`}>
        <StyledDataColumn>{code}</StyledDataColumn>
        <StyledDataColumn>{startCase(brandName)}</StyledDataColumn>
        <StyledDataColumn>
          {startCase(productName.replace(/_/g, ' '))}
        </StyledDataColumn>
        <StyledDataColumn>
          {startCase(discountType.replace(/_/g, ' '))}
        </StyledDataColumn>
        <StyledDataColumn>
          {formatDiscount(discountType, discountValue)}
        </StyledDataColumn>
        <StyledDataColumn>
          {expiresAt ? (
            <>
              {dayjsLondon(expiresAt).format('D MMM YYYY')}
              <br />
              {dayjsLondon(expiresAt).format('HH:mm')}
            </>
          ) : (
            '—'
          )}
        </StyledDataColumn>
        <StyledCenteredDataColumn>{usageLimit ?? '∞'}</StyledCenteredDataColumn>
        <StyledCenteredDataColumn>{usage}</StyledCenteredDataColumn>
        <StyledCenteredDataColumn>{referrals}</StyledCenteredDataColumn>
      </RowLink>
    </VoucherDataRow>
  )
}

const VouchersList = (): React.ReactElement => {
  const { apiSearchParams, filters, filterHandler, sort, sortHandler } =
    useSearchParamManager(INITIAL_FILTER_VALUES, SORT_CONFIG, {
      storageEnabled: false,
      storageKey: '',
    })

  const { data: vouchersResponse, isLoading } = useFetchArrayResource<Voucher>({
    resource: RESOURCES.VOUCHERS,
    params: apiSearchParams,
  })

  const vouchers = vouchersResponse?.data

  return (
    <Wrapper>
      <Grid gap="L">
        <Grid.Item span={2}>
          {filters && (
            <Formik initialValues={filters} onSubmit={filterHandler}>
              <FilterPanel />
            </Formik>
          )}
        </Grid.Item>
        <StickyGridItem span={10} padding={['L', 0, 0]}>
          <StyledHeaderRow>
            {!isLoading && (
              <P size="L" margin={0} strong>
                {(vouchers?.length ?? 0) === 1
                  ? '1 voucher code'
                  : `${vouchers?.length ?? 0} voucher codes`}
              </P>
            )}
            <StyledCreateButton>
              <Button.Primary tag={Link} to="/admin/vouchers/new">
                Create voucher code
              </Button.Primary>
            </StyledCreateButton>
          </StyledHeaderRow>
          <VoucherHeaderRow header gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            <SortableColumn
              columnTitle="Code"
              columnSortValues={{ sortColumn: 'code', sortOrder: 'asc' }}
              setSort={sortHandler}
              sortedValues={sort}
            />
            <SortableColumn
              columnTitle="Brand"
              columnSortValues={{ sortColumn: 'brandId', sortOrder: 'asc' }}
              setSort={sortHandler}
              sortedValues={sort}
            />
            <SortableColumn
              columnTitle="Product"
              columnSortValues={{ sortColumn: 'productName', sortOrder: 'asc' }}
              setSort={sortHandler}
              sortedValues={sort}
            />
            <SortableColumn
              columnTitle="Voucher type"
              columnSortValues={{
                sortColumn: 'discountType',
                sortOrder: 'asc',
              }}
              setSort={sortHandler}
              sortedValues={sort}
            />
            <StyledGridColumn>Amount</StyledGridColumn>
            <SortableColumn
              columnTitle="Expires"
              columnSortValues={{ sortColumn: 'expiresAt', sortOrder: 'asc' }}
              setSort={sortHandler}
              sortedValues={sort}
            />
            <StyledCenteredColumn>Usage Limit</StyledCenteredColumn>
            <StyledCenteredColumn>Usage</StyledCenteredColumn>
            <StyledCenteredColumn>
              Referrals
              <br />
              (incl. Lost)
            </StyledCenteredColumn>
          </VoucherHeaderRow>
          {isLoading && <StyledLoadingRow>Loading...</StyledLoadingRow>}
          {!isLoading && !vouchers?.length && (
            <NoResults type="voucher codes" />
          )}
          {!isLoading &&
            vouchers?.map((voucher: Voucher) => (
              <Row key={voucher.attributes.id} voucher={voucher} />
            ))}
        </StickyGridItem>
      </Grid>
    </Wrapper>
  )
}

export default VouchersList
